import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import TwAutocomplete, {
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import { PatternFormat } from 'react-number-format';
import useAuth from '../../../providers/Auth/Auth.provider';

import { UpdateLocationData } from './../../../redux/api/location/location.model';

import useGoogleGeocoder from '../../../hooks/useGoogleGeocoder';
import useGooglePlacesAutocomplete from '../../../hooks/useGooglePlacesAutocomplete';

import {
  CAN_STATES,
  CountryCode,
  MX_STATES,
  supportedCountries,
  timezonesSupported,
  US_STATES
} from '../../../utils/regionUtils';
import {
  useUpdateLocationMutation,
  useGetLocationByIdQuery
} from './../../../redux/api/location/locationApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import {
  getAddressLines,
  locationTypeOptions,
  makeFullAddress
} from '../../../utils/location/location';
import {
  Chip,
  Stack,
  Typography,
  Box,
  Grid,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  Theme,
  Divider
} from '@mui/material';
import TwMap from '../../../components/TwMap/TwMap';
import TwMarker, {
  LocationMarker,
  LocationMarkerType
} from '../../../components/TwMarker/TwMarker';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import {
  getLatLngFromGoogleMapsLink,
  getMarkerIcon,
  validateGoogleMapsLink
} from '../../../utils/mapUtils';
import './EditLocation.scss';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import UtilService from '../../../providers/Util/Util.service';
import MissingAlert from '../components/MissingAlert';
import useMissingLocationInfo from '../hooks/useMissingLocationInfo';
import { CreateLocationFormValues as EditLocationFormValues } from '../CreateLocation/CreateLocation';
import { Characters, useUnformatValue } from '../../../hooks/useAllowCharacters';
import { LoadLocation, LOCATION_TYPE } from '../../../redux/api/load/load.model';
import useGoogleMaps from '../../../providers/GoogleMaps/GoogleMaps.provider';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { LocationTypeFields } from '../components/LocationTypeFields';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { usePortCodeOptions } from '../../../hooks/usePortOfEntryOptions';
import { EntityType } from '../../../redux/Global.model';
import useCheckDriverPay from '../../../hooks/useCheckDriverPay';
import { updateTripLtlDriverPayConfirmationModal } from '../../../redux/features/trip/tripSlice';
import { updateDriverPayConfirmationModal } from '../../../redux/features/orders/ordersSlice';
import { logAmplitudeEvent } from '../../../utils/amplitudeUtils';
import { AmplitudeEvent } from '../../../utils/amplitudeUtils';

interface EditLocationProps {
  open: boolean;
  onOpen?: () => void;
  onClose: () => void;
  onSuccess?: (newLocation: LoadLocation, clearDriverPay: boolean) => void;
  locationId: string;
  entity?: EntityType;
}

const initialValues: EditLocationFormValues = {
  address_1: '',
  address_2: '',
  city: '',
  country: null,
  state: null,
  zip: '',
  locationName: '',
  google_place_id: '',
  is_zip_approximated: false,
  full_address: '',
  is_appointment_required: false,
  notes: '',
  contact_name: '',
  contact_number: '',
  contact_number_ext: '',
  contact_email: '',
  customer: null,
  type: null,
  port_code: null,
  clearDriverPay: false,
  timezone: null
};
const DEFAULT_MAP_CENTER = { lat: 39.7567241, lng: -101.6269877 };

const EditLocation: FC<EditLocationProps> = ({ open, onClose, onSuccess, locationId, entity }) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { enableBorderConnect, enableHideGoogleMapsAndRoutingCalculations } = useFeatureFlags();
  const dispatch = useAppDispatch();
  const { loaded: googleMapsLoaded } = useGoogleMaps({
    loadScript: enableHideGoogleMapsAndRoutingCalculations ? false : true
  });
  const geocoder = useGoogleGeocoder();
  const { init: initAutocomplete, setComponentRestrictions: setAutocompleteRestrictions } =
    useGooglePlacesAutocomplete();
  const anyDriverPayExists = useCheckDriverPay({ entity: entity || null });

  const { order_id: load_id, initial_location_ids } = useAppSelector((state) => state.orders);
  const isEditLoad = !!load_id;
  const isLocationAssociatedWithCurrentLoad =
    isEditLoad &&
    initial_location_ids &&
    initial_location_ids.length > 0 &&
    initial_location_ids.includes(locationId);

  const [updateLocation] = useUpdateLocationMutation();
  const { data: location, error: locationError } = useGetLocationByIdQuery(locationId, {
    skip: !locationId
  });

  const { addNotification } = useNotifications();

  const unformatString = useUnformatValue(Characters.number);
  const autocompleteInputRef = useRef<HTMLInputElement | undefined>();
  const isAutocompleteInUseRef = useRef<boolean>(false);
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [countries, setCountries] = useState<TwAutocompleteOption[]>([]);
  const [displayExtraInfo, setDisplayExtraInfo] = useState<boolean>(false);
  const [googleAutocompleteText, setGoogleAutocompleteText] = useState<string>('');
  const [googleMapsLink, setGoogleMapsLink] = useState<string>('');
  const [googleMapsLongLink, setGoogleMapsLongLink] = useState<string>('');
  const [googleMapsLinkError, setGoogleMapsLinkError] = useState<boolean>(false);
  const [loadingMapsLink, setLoadingMapsLink] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(CountryCode.US);
  const [states, setStates] = useState<TwAutocompleteOption[]>([]);
  const [searchForAddress, setSearchForAddress] = useState<boolean>(true);

  const [zipcodeError, setZipcodeError] = useState(false);
  const isMultiCountryActive = getTenantSetting('multi-country', false);
  // Listen to zip changes to prevent unwanted geocoder search
  const [zipEdited, setZipEdited] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<LocationMarker | null>(null);
  const [isUsedByOrders, setIsUsedByOrders] = useState<number | undefined>(undefined);
  const [timezones, setTimezones] = useState<TwAutocompleteOption[]>([]);
  const mapRef = useRef<HTMLDivElement | null>(null);
  let typingTimer: any = null;
  const doneTypingInterval = 1000;
  const { values, handleChange, handleSubmit, resetForm, setFieldValue } =
    useFormik<EditLocationFormValues>({
      initialValues: initialValues,
      onSubmit: (values: EditLocationFormValues): void => submitLocation(values)
    });

  const isAddressComponentsMissing = useMissingLocationInfo(values);
  const { usPorts, caPorts } = usePortCodeOptions();

  const getCountry = (countryName: string): CountryCode => {
    const country = supportedCountries.find((c) => c.name === countryName);
    return country?.key || CountryCode.US;
  };

  useEffect(() => {
    const newCountries: TwAutocompleteOption[] = supportedCountries.map((country) => ({
      id: country.key,
      label: country.name,
      value: country.key
    }));
    setCountries(newCountries);
  }, []);

  useEffect(() => {
    if (open && enableHideGoogleMapsAndRoutingCalculations) {
      const autocompleteTimezones = timezonesSupported.map((timezone) => ({
        id: timezone.key,
        label: timezone.name,
        value: timezone.key
      }));
      setTimezones(autocompleteTimezones);
    }
  }, [open]);

  useEffect(() => {
    if (locationError) {
      console.error(locationError);
      addNotification({
        message: 'Error getting location data, please try again.',
        variant: 'error'
      });
    }
  }, [locationError]);

  useEffect(() => {
    if (open && location && geocoder) {
      (async (): Promise<void> => {
        const country = getCountry(location.country);
        setCountryAndStates(country, true);
        setIsUsedByOrders(location.isUsedByOrders);
        setDisplayExtraInfo(true);

        const addresses = getAddressLines(location.full_address || '', location.city);

        const ports = location.type === LOCATION_TYPE.US_PORT ? usPorts : caPorts;
        // Set field values from location
        setFieldValue('address_1', location.address_1 || addresses.address1);
        setFieldValue('address_2', location.address_2 || addresses.address2);
        setFieldValue('city', location.city);
        setFieldValue('zip', location.zip);
        setFieldValue('full_address', location.full_address);
        setFieldValue('locationName', location.name);
        setFieldValue('is_appointment_required', location.is_appointment_required);
        setFieldValue('notes', location.notes);
        setFieldValue('contact_name', location.contact_name);
        setFieldValue('contact_email', location.contact_email);
        setFieldValue('contact_number', location.contact_number);
        setFieldValue('contact_number_ext', location.contact_number_ext);
        setFieldValue(
          'type',
          locationTypeOptions(enableBorderConnect).find((type) => type.value === location.type) ??
            null
        );
        setFieldValue(
          'port_code',
          ports.find((port) => port.value === location.port_code)
        );

        if (location.customers && location.customers[0]) {
          setFieldValue('customer', {
            id: location.customers[0].id,
            label: location.customers[0].name,
            value: location.customers[0].id
          });
        }

        // check if the location already has lat and lng to use in marker
        if (location.latitude && location.longitude) {
          setMarker({
            position: new google.maps.LatLng(location.latitude, location.longitude),
            type: LocationMarkerType.STOP_CIRCLE
          });
        } else {
          // location does not have a lat and lng, so we need to get it from the address
          // todo: move this part of the gecode request to a reusable function
          let addressToSearch = `${location.address_1}`;
          if (location.address_2) addressToSearch += `, ${location.address_2}`;
          if (location.zip) addressToSearch += `, ${location.zip}`;

          logAmplitudeEvent(AmplitudeEvent.GOOGLE_GEOCODER_REQUEST);
          geocoder
            .geocode({
              region: country,
              address: addressToSearch
            })
            .then((geocoderResponse) => {
              const { results } = geocoderResponse;

              if (!results) {
                setMarker(null);
              } else {
                // todo: we need to cover the cases where there are more than one result, because we are only using the first one
                // whick leads the marker to be off the actual location sometimes
                setMarker({
                  position: results[0].geometry.location,
                  type: LocationMarkerType.STOP_CIRCLE
                });
              }
            })
            .catch((e) => {
              console.error(e);
              addNotification({
                message: 'Google geocode unable to find location',
                variant: 'error'
              });
            });
        }
      })();
    }
  }, [location, open, geocoder]);

  const setCountryAndStates = (country: CountryCode, isFirstTime: boolean): void => {
    let statesToMap: string[] = [];

    switch (country) {
      case CountryCode.US:
        statesToMap = US_STATES;
        break;
      case CountryCode.MX:
        statesToMap = MX_STATES;
        break;
      case CountryCode.CAN:
        statesToMap = CAN_STATES;
        break;
      default:
        statesToMap = [];
    }

    const newStates = statesToMap.map((state) => ({
      id: state,
      label: state,
      value: state
    }));

    setAutocompleteRestrictions({ country: [country] });
    setFieldValue(
      'country',
      countries.find((c) => c.id === country)
    );
    setSelectedCountry(country);
    setStates(newStates);

    if (isFirstTime) {
      const locationState = newStates.find((s) => s.value === location?.state);
      locationState && setFieldValue('state', locationState || '');
    }
  };

  useEffect(() => {
    const autocompleteTimeout = setTimeout(() => {
      // Init Google autocomplete
      initAutocomplete({
        inputRef: autocompleteInputRef,
        countries: isMultiCountryActive
          ? [CountryCode.US, CountryCode.CAN, CountryCode.MX]
          : [CountryCode.US],
        onPlaceChanged: onGooglePlaceChanged
      });

      autocompleteInputRef.current?.focus();
    }, 100);

    return (): void => {
      clearTimeout(autocompleteTimeout);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !searchForAddress) return;

    const autocompleteTimeout = setTimeout(() => {
      // Init Google autocomplete
      initAutocomplete({
        inputRef: autocompleteInputRef,
        countries: isMultiCountryActive
          ? [CountryCode.US, CountryCode.CAN, CountryCode.MX]
          : [CountryCode.US],
        onPlaceChanged: onGooglePlaceChanged
      });

      autocompleteInputRef.current?.focus();
    }, 100);

    return (): void => {
      clearTimeout(autocompleteTimeout);
    };
  }, [searchForAddress]);

  useEffect(() => {
    if (!geocoder || isAutocompleteInUseRef.current || !zipEdited) {
      return;
    }

    if (
      (values.zip.length !== 5 || !values.zip) &&
      !location?.latitude &&
      !location?.longitude &&
      values.country?.id !== CountryCode.CAN
    ) {
      setMarker(null);
      return;
    }

    if (
      !values.zip &&
      !location?.latitude &&
      !location?.longitude &&
      values.country?.id === CountryCode.CAN
    ) {
      setMarker(null);
      return;
    }

    (async (): Promise<void> => {
      logAmplitudeEvent(AmplitudeEvent.GOOGLE_GEOCODER_REQUEST);
      const geocoderResponse = await geocoder.geocode({
        region: values.country?.id || selectedCountry,
        address: `${values.address_1} zipcode ${values.zip}`
      });

      const result = geocoderResponse.results.length > 0 ? geocoderResponse.results[0] : null;

      if (!result) {
        setMarker(null);
        return;
      }

      let city = '';
      let state = '';

      result.address_components.forEach((addressComponent) => {
        if (!city && addressComponent.types.includes('locality')) {
          city = addressComponent.short_name;
        }

        if (!state && addressComponent.types.includes('administrative_area_level_1')) {
          state = addressComponent.short_name;
        }
      });
      setMarker({ position: result.geometry.location, type: LocationMarkerType.LOCATION_PIN });
      setFieldValue('google_place_id', result.place_id);
      setFieldValue('city', city);

      if (!!state) {
        setFieldValue('state', {
          id: state,
          label: state,
          value: state
        });
      }
      setFieldValue('is_zip_approximated', true);

      setGoogleAutocompleteText('');
    })();
  }, [geocoder, values.zip]);

  useEffect(() => {
    const isLinkValid = validateGoogleMapsLink(googleMapsLink.trim());
    if (googleMapsLink === '' || isLinkValid) {
      setGoogleMapsLongLink(googleMapsLink.trim());
      setGoogleMapsLinkError(false);
      return;
    }

    UtilService.getLongUrl(googleMapsLink.trim())
      .then((data) => {
        if (!validateGoogleMapsLink(data.longUrl)) {
          throw new Error('Invalid Url');
        }
        setGoogleMapsLongLink(data.longUrl);
        setGoogleMapsLinkError(false);
      })
      .catch(() => {
        setGoogleMapsLinkError(true);
        setLoadingMapsLink(false);
      });
  }, [googleMapsLink]);

  useEffect(() => {
    if (!geocoder || searchForAddress || !validateGoogleMapsLink(googleMapsLongLink)) return;

    (async (): Promise<void> => {
      const latLng = getLatLngFromGoogleMapsLink(googleMapsLongLink);

      if (!latLng) return;
      setLoadingMapsLink(true);

      setMarker({ position: latLng, type: LocationMarkerType.STOP_CIRCLE });
      logAmplitudeEvent(AmplitudeEvent.GOOGLE_GEOCODER_REQUEST);
      const geocoderResponse = await geocoder.geocode({
        location: latLng
      });

      const result = geocoderResponse.results.length > 0 ? geocoderResponse.results[0] : null;

      if (!result) return;

      let address = '';
      let city = '';
      let state = '';
      let zip = '';
      let country = '';

      for (const component of result.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'street_number':
            address = `${component.long_name} ${address}`;
            break;
          case 'route':
            address += component.short_name;
            break;
          case 'postal_code':
            zip = `${component.long_name}${zip}`;
            break;
          case 'locality':
            city = component.long_name;
            break;
          case 'administrative_area_level_1':
            state = component.short_name;
            break;
          case 'country':
            country = component.long_name;
            break;
        }
      }
      const addresses = getAddressLines(result.formatted_address || '', city);
      const countryObj = countries.find((c) => c.label.toLowerCase === country.toLowerCase) || null;
      if (countryObj) {
        setFieldValue('country', countryObj);
      }
      setGoogleAutocompleteText(result.formatted_address || '');
      setFieldValue('google_place_id', result.place_id);
      setFieldValue('address_1', addresses.address1 || address);
      setFieldValue('address_2', addresses.address2);
      setFieldValue('city', city);
      setFieldValue('zip', zip);

      if (!!state) {
        setFieldValue('state', {
          id: state,
          label: state,
          value: state
        });
      }
      setFieldValue('is_zip_approximated', false);
      setFieldValue('full_address', result.formatted_address);
      setDisplayExtraInfo(true);
      setLoadingMapsLink(false);
    })().catch((e) => {
      console.error('Error getting location from link!', e);
      logRocketTrack('Load build', {
        action: 'Edit location from google maps link',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Error finding location from link',
        variant: 'error'
      });
      setLoadingMapsLink(false);
    });
  }, [geocoder, googleMapsLongLink]);

  useEffect(() => {
    if (marker && open && map) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(marker.position);
      map?.fitBounds(bounds);
      map?.setZoom(18);
    }
  }, [marker, open, map]);

  const submitLocation = (values: EditLocationFormValues): void => {
    // Editing locations, if they have a zip, we prevent users from saving the location if new zip is not valid or user deletes it
    if (
      (!values.zip || values.zip.length !== 5 || values.zip.toLowerCase().includes('e')) &&
      !!location?.zip &&
      values.country?.id !== CountryCode.CAN
    ) {
      setZipcodeError(true);
      logRocketTrack('Edit location', {
        action: 'Edit location',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({ message: 'Please enter a valid zip code', variant: 'error' });
      return;
    }

    if (!values.zip && !!location?.zip && values.country?.id === CountryCode.CAN) {
      setZipcodeError(true);
      logRocketTrack('Edit location', {
        action: 'Edit location',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({ message: 'Please enter a valid zip code', variant: 'error' });
      return;
    }

    if (!values.city || !values.state) {
      logRocketTrack('Load build', {
        action: 'Edit location from google maps link',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({ message: 'City or state are missing', variant: 'error' });
      return;
    }

    // Making full address from form values
    // in case user makes a change in the default address
    const full_address = makeFullAddress({
      address_1: values.address_1?.trim(),
      address_2: values.address_2?.trim(),
      city: values.city,
      state: values.state?.id || '',
      zip: values.zip,
      country: values.country?.id || ''
    });

    const data: UpdateLocationData = {
      name: values.locationName ? values.locationName?.trim() : values.full_address,
      address_1: values.address_1,
      full_address,
      city: values.city,
      state: values.state?.id || '',
      is_zip_approximated: values.is_zip_approximated,
      id: locationId,
      is_appointment_required: values.is_appointment_required,
      contact_name: values.contact_name?.trim(),
      contact_email: values.contact_email?.trim(),
      contact_number: unformatString(values.contact_number || ''),
      contact_number_ext: values.contact_number_ext,
      notes: values.notes?.trim(),
      customer_id: values.customer?.id,
      type: (values.type?.value as LOCATION_TYPE) ?? null,
      port_code: values.port_code?.id ?? null
    };

    if (!!values.address_2) {
      data.address_2 = values.address_2?.trim();
    }

    if (values.google_place_id !== '') {
      data.google_place_id = values.google_place_id;
    }

    if (values.zip !== '') {
      data.zip = values.zip;
    }

    switch (data.type) {
      case LOCATION_TYPE.CUSTOMER:
        data.port_code = null;
        break;
      case LOCATION_TYPE.TERMINAL:
        data.customer_id = undefined;
        data.port_code = null;
        break;
      case LOCATION_TYPE.CA_PORT:
      case LOCATION_TYPE.US_PORT:
        data.customer_id = undefined;
        break;
    }

    updateLocation(data)
      .unwrap()
      .then((payload) => {
        onSuccess && onSuccess(payload, values.clearDriverPay);
        handleClose();
      })
      .catch((error) => {
        addNotification({
          message: error.data.message,
          variant: 'error',
          position: 'bottomRight',
          timeToShow: 3000
        });
      });
  };

  const handleClose = (): void => {
    onClose();
    setGoogleAutocompleteText('');
    setZipcodeError(false);
    setZipEdited(false);
    resetForm();
    setMap(undefined);
    mapRef.current = null;
  };

  const onGooglePlaceChanged = (place?: google.maps.places.PlaceResult): void => {
    if (!place || !place.address_components) return;
    logAmplitudeEvent(AmplitudeEvent.GOOGLE_PLACES_REQUEST);

    isAutocompleteInUseRef.current = true;

    let address = '';
    let city = '';
    let state = '';
    let zip = '';

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          address = `${component.long_name} ${address}`;
          break;
        case 'route':
          address += component.short_name;
          break;
        case 'postal_code':
          zip = `${component.long_name}${zip}`;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name;
          break;
      }
    }
    const addresses = getAddressLines(place.formatted_address || '', city);
    place.geometry?.location &&
      setMarker({ position: place.geometry.location, type: LocationMarkerType.LOCATION_PIN });
    setGoogleAutocompleteText(place.formatted_address || '');
    setFieldValue('google_place_id', place.place_id);
    setFieldValue('address_1', addresses.address1 || address);
    setFieldValue('address_2', addresses.address2);
    setFieldValue('city', city);
    setFieldValue('zip', zip);

    if (!!state) {
      setFieldValue('state', {
        id: state,
        label: state,
        value: state
      });
    }
    setFieldValue('is_zip_approximated', false);
    setFieldValue('full_address', place.formatted_address);
    setDisplayExtraInfo(true);
    isAutocompleteInUseRef.current = false;
  };

  const doneTyping = async (): Promise<void> => {
    if (!geocoder || isAutocompleteInUseRef.current) {
      return;
    }

    let addressQuery = `${values.address_1}`;
    if (values.zip) {
      addressQuery = `${values.address_1} zipcode ${values.zip}`;
    }
    logAmplitudeEvent(AmplitudeEvent.GOOGLE_GEOCODER_REQUEST);
    const geocoderResponse = await geocoder.geocode({
      region: values.country?.id || selectedCountry,
      address: addressQuery
    });

    const result = geocoderResponse.results.length > 0 ? geocoderResponse.results[0] : null;

    if (!result) {
      setMarker(null);
      return;
    }

    let city = '';
    let state = '';

    result.address_components.forEach((addressComponent) => {
      if (!city && addressComponent.types.includes('locality')) {
        city = addressComponent.short_name;
      }

      if (!state && addressComponent.types.includes('administrative_area_level_1')) {
        state = addressComponent.short_name;
      }
    });

    setFieldValue('google_place_id', result.place_id);
    setFieldValue('city', city);
    setMarker({ position: result.geometry.location, type: LocationMarkerType.STOP_CIRCLE });

    if (!!state) {
      setFieldValue('state', {
        id: state,
        label: state,
        value: state
      });
    }
    setFieldValue('is_zip_approximated', true);
    setZipcodeError(false);
    setGoogleAutocompleteText('');
  };

  const handleOnKeyUp = (): void => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(doneTyping, doneTypingInterval);
  };

  const handleKeyDown = (): void => {
    clearTimeout(typingTimer);
  };

  /**
   * We want to disable the location fields if:
   * 1. The location is used by multiple loads
   * 2. The location is already used by a single load and the user is creating a new load with this location
   * 3. The location is already used by a single load and the user is editing a load that is not associated with this location
   */
  const disableLocationFields: boolean =
    !!isUsedByOrders &&
    (isUsedByOrders > 1 ||
      (isUsedByOrders === 1 && !isEditLoad) ||
      (isUsedByOrders === 1 && isEditLoad && !isLocationAssociatedWithCurrentLoad));

  /**
   * We want to know if the address fields have been changed
   * because these could affect mileage and in consequence, the driver's pay
   */
  const areAddressFieldsChanged = useMemo((): boolean => {
    if (!location) return false;

    // Compare only address-related fields
    const addressChanged =
      values.address_1 !== location.address_1 ||
      values.address_2 !== location.address_2 ||
      values.zip !== location.zip ||
      values.city !== location.city ||
      values.state?.id !== location.state ||
      values.country?.label !== location.country;

    return addressChanged;
  }, [location, values]);

  return (
    <TwDialog
      open={open}
      onClickClose={handleClose}
      size="md"
      title="Edit Location"
      actions={[
        <TwButton
          key="1"
          fullwidth={false}
          type={TwButtonType.secondary}
          label="Cancel"
          action={handleClose}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          type={TwButtonType.primary}
          label="Save"
          action={(): void => {
            if (anyDriverPayExists && entity && areAddressFieldsChanged) {
              setFieldValue('clearDriverPay', true);
              if (enableLtl && entity === EntityType.TRIP) {
                dispatch(
                  updateTripLtlDriverPayConfirmationModal({
                    open: true,
                    action: () => handleSubmit()
                  })
                );
              } else {
                dispatch(
                  updateDriverPayConfirmationModal({
                    open: true,
                    action: () => handleSubmit()
                  })
                );
              }
            } else {
              setFieldValue('clearDriverPay', false);
              handleSubmit();
            }
          }}
        />
      ]}
      draggable={true}
    >
      <Stack flexDirection={matchesMobileOrTablet ? 'column' : 'row'} gap={2}>
        <Box
          component="form"
          className={
            disableLocationFields
              ? 'edit-location__wrapper edit-location__disabled-fields '
              : 'edit-location__wrapper'
          }
        >
          {!!isUsedByOrders && isUsedByOrders === 1 && disableLocationFields && (
            <Typography className="edit-location__usedbyloads-message">
              {`Editing this location is available on the load page in which this location's address is linked to.`}
            </Typography>
          )}
          {!!isUsedByOrders && isUsedByOrders > 1 && (
            <Typography className="edit-location__usedbyloads-message">
              This location is being used in more than 1 load and the address cannot be changed.
            </Typography>
          )}
          {!enableHideGoogleMapsAndRoutingCalculations &&
            searchForAddress &&
            !disableLocationFields && (
              <TwField
                inputRef={autocompleteInputRef}
                id="address-autocomplete"
                name="address-autocomplete"
                placeholder="Search for address..."
                value={googleAutocompleteText}
                onInputChange={(e): void => setGoogleAutocompleteText(e.target.value)}
                autoComplete="off"
                startAdorment={
                  <InputAdornment position="start">
                    <SearchIcon height={14} />
                  </InputAdornment>
                }
                endAdorment={
                  <InputAdornment
                    position="end"
                    onClick={(): void => {
                      setGoogleMapsLink('');
                      setGoogleMapsLongLink('');
                      setSearchForAddress(false);
                    }}
                    sx={{
                      cursor: 'pointer'
                    }}
                  >
                    {' '}
                    <TwTooltip title="Use Google Maps pin" placement="top">
                      <PublicIcon height={14} />
                    </TwTooltip>
                  </InputAdornment>
                }
              />
            )}

          {!enableHideGoogleMapsAndRoutingCalculations &&
            !searchForAddress &&
            !disableLocationFields && (
              <>
                <TwField
                  id="google-maps-link"
                  name="google-maps-link"
                  placeholder="Paste Google Maps link"
                  value={googleMapsLink}
                  error={googleMapsLinkError}
                  onInputChange={(e): void => setGoogleMapsLink(e.target.value)}
                  autoComplete="off"
                  startAdorment={
                    <InputAdornment position="start">
                      <PublicIcon height={14} />
                    </InputAdornment>
                  }
                  endAdorment={
                    <InputAdornment
                      position="end"
                      onClick={(): void => {
                        setGoogleAutocompleteText('');
                        setGoogleMapsLinkError(false);
                        setSearchForAddress(true);
                      }}
                      sx={{
                        cursor: 'pointer'
                      }}
                    >
                      {loadingMapsLink ? (
                        <CircularProgress color="inherit" size={14} />
                      ) : (
                        <SearchIcon height={14} />
                      )}
                    </InputAdornment>
                  }
                />
                {googleMapsLinkError && (
                  <Box
                    sx={{
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#B91C1C',
                      paddingBottom: '8px'
                    }}
                  >
                    Location not found. Check link or try another location.
                  </Box>
                )}
              </>
            )}
          {!displayExtraInfo && (
            <Box className="create-location__info-text">
              Can{"'"}t find a location?{' '}
              <Box component="a" onClick={(): void => setDisplayExtraInfo(true)}>
                Add it manually
              </Box>
            </Box>
          )}
          {isAddressComponentsMissing && <MissingAlert />}

          <TwField
            id="locationName"
            name="locationName"
            label="Location name"
            value={values.locationName}
            onInputChange={handleChange}
            autoComplete="off"
          />
          {isMultiCountryActive && (
            <TwAutocomplete
              id="country"
              name="country"
              label="Country"
              disabled={disableLocationFields}
              optionList={countries}
              value={values.country}
              onChange={(_, newValue: TwAutocompleteOption | null): void => {
                setFieldValue('google_place_id', '');
                setFieldValue('address_1', '');
                setFieldValue('address_2', '');
                setFieldValue('city', '');
                setFieldValue('zip', '');
                setFieldValue('is_zip_approximated', false);
                setFieldValue('full_address', '');
                setCountryAndStates((newValue?.id as CountryCode) || CountryCode.US, false);
              }}
            />
          )}

          {!displayExtraInfo && (
            <Box className="edit-location__info-text">
              Can{"'"}t find a location?{' '}
              <Box component="a" onClick={(): void => setDisplayExtraInfo(true)}>
                Add it manually
              </Box>
            </Box>
          )}

          {displayExtraInfo && (
            <>
              <TwField
                id="address_1"
                name="address_1"
                label="Address"
                placeholder="Street address, apt., suite, building, floor, etc."
                value={values.address_1}
                onInputChange={handleChange}
                autoComplete="off"
                onKeyDown={handleKeyDown}
                onKeyUp={handleOnKeyUp}
                error={isAddressComponentsMissing && !values.address_1}
                disabled={disableLocationFields}
              />
              <TwField
                id="address_2"
                name="address_2"
                label="Address line 2"
                placeholder={
                  selectedCountry === CountryCode.US ? 'Optional' : 'Neighborhood or Municipality'
                }
                value={values.address_2}
                onInputChange={handleChange}
                autoComplete="off"
                disabled={disableLocationFields}
              />
              <Grid container spacing={2}>
                <Grid item xs={matchesMobileOrTablet ? 12 : 6}>
                  <TwField
                    id="city"
                    name="city"
                    label="City"
                    value={values.city}
                    onInputChange={handleChange}
                    autoComplete="off"
                    error={isAddressComponentsMissing && !values.city}
                    disabled={disableLocationFields}
                  />
                </Grid>
                <Grid item xs={matchesMobileOrTablet ? 6 : 3}>
                  <TwAutocomplete
                    id="state"
                    name="state"
                    label="State"
                    optionList={states}
                    sortList={false}
                    value={values.state}
                    onChange={(
                      _,
                      newValue: TwAutocompleteOption | null
                    ): Promise<void> | Promise<FormikErrors<EditLocationFormValues>> => {
                      return setFieldValue('state', newValue);
                    }}
                    error={isAddressComponentsMissing && !values.state}
                    disabled={disableLocationFields}
                  />
                </Grid>
                <Grid item xs={matchesMobileOrTablet ? 6 : 3}>
                  <TwField
                    id="zip"
                    name="zip"
                    label="Zip"
                    value={values.zip}
                    onInputChange={(
                      e
                    ): Promise<void> | Promise<FormikErrors<EditLocationFormValues>> => {
                      setZipEdited(true);
                      return setFieldValue(
                        'zip',
                        e.target.value.length > 5 && selectedCountry !== CountryCode.CAN
                          ? values.zip
                          : e.target.value
                      );
                    }}
                    autoComplete="off"
                    inputType={
                      selectedCountry === CountryCode.CAN ? TwInputTypes.text : TwInputTypes.number
                    }
                    error={zipcodeError}
                    disabled={disableLocationFields}
                  />
                </Grid>
              </Grid>
              {enableHideGoogleMapsAndRoutingCalculations && (
                <Grid container>
                  <Grid item xs={9}>
                    <TwAutocomplete
                      id="timezone"
                      name="timezone"
                      label="Timezone"
                      optionList={timezones}
                      sortList={false}
                      value={values.timezone}
                      onChange={(
                        _,
                        newValue: TwAutocompleteOption | null
                      ): Promise<void> | Promise<FormikErrors<EditLocationFormValues>> => {
                        return setFieldValue('timezone', newValue);
                      }}
                      error={isAddressComponentsMissing && !values.timezone}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Box>
        <Box className="create-location__container-preview-map">
          {!marker && !enableHideGoogleMapsAndRoutingCalculations && (
            <Box className="create-location__container-preview-map-greyed">
              <Chip
                className="create-location__container-preview-map-chip"
                label={'Location preview unavailable'}
              />
            </Box>
          )}
          {!enableHideGoogleMapsAndRoutingCalculations && googleMapsLoaded && (
            <TwMap
              map={map}
              mapRef={mapRef}
              setMap={setMap}
              center={marker ? marker.position : DEFAULT_MAP_CENTER}
              className="edit-location__preview-map"
              disableDefaultUI
              zoom={3}
            >
              {marker && (
                <TwMarker
                  position={marker.position}
                  map={map}
                  icon={getMarkerIcon(LocationMarkerType.LOCATION_PIN)}
                />
              )}
            </TwMap>
          )}

          <Typography className="create-location__subtitle">
            Location-specific instructions
          </Typography>
          <TwCheckbox
            onInputChange={handleChange}
            label="Appointment required"
            name="is_appointment_required"
            checked={values.is_appointment_required}
            value={values.is_appointment_required}
          />
          <LocationTypeFields
            values={values}
            setFieldValue={setFieldValue}
            customerDisabled={
              disableLocationFields && location?.customers && location?.customers?.length > 0
            }
          />
          <TwField
            id="instructions"
            name="notes"
            label="Location instructions"
            inputType={TwInputTypes.textarea}
            onInputChange={handleChange}
            value={values.notes}
          />
          <TwField
            id="contact_name"
            name="contact_name"
            label="Contact name"
            value={values.contact_name}
            onInputChange={handleChange}
          />
          <Grid container justifyContent="space-between">
            <Grid item md={5.5} xs={12}>
              <TwField
                id="email"
                name="contact_email"
                label="Email"
                onInputChange={handleChange}
                value={values.contact_email}
              />
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={6}
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <Grid item xs={8}>
                <PatternFormat
                  format="(###) ###-####"
                  id="phone"
                  name="contact_number"
                  value={values.contact_number}
                  customInput={TwField}
                  onInputChange={handleChange}
                  displayType="input"
                  label="Phone"
                  placeholder="(___) ___-____"
                />
              </Grid>
              <Grid item xs={4}>
                <TwField
                  id="phone_ext"
                  name="contact_number_ext"
                  onInputChange={handleChange}
                  value={values.contact_number_ext}
                  placeholder="Ext."
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </TwDialog>
  );
};

export default EditLocation;
